import type { GeometryKey } from './keys';

export const nl: Record<GeometryKey, string> = {
  // 9 Experiments
  'experiment.angle-chase': 'Hoekenjacht',
  'experiment.triangle-lab': 'Driehoeklab',
  'experiment.pythagoras': 'Pythagoras',
  'experiment.similarity': 'Gelijkvormigheid',
  'experiment.circle-theorems': 'Cirkelstellingen',
  'experiment.area-lab': 'Oppervlaktelab',
  'experiment.transformations': 'Transformaties',
  'experiment.coordinate-geometry': 'Coördinaatmeetkunde',
  'experiment.constructions': 'Constructies',
  // Common controls
  'action.reset': 'Opnieuw',
  'action.randomise': 'Willekeurig',
  'action.show-me': 'Toon mij',
  'action.undo': 'Ongedaan maken',
  'action.eraser': 'Gummetje',
  'panel.advanced': 'Geavanceerd',
  'panel.mode': 'Modus',
  'panel.tool': 'Gereedschap',
  // Shared chrome — eyebrow + interaction hint
  'app.eyebrow': 'Geometrie · 2D',
  'hint.line1': 'Sleep de gekleurde punten om te verkennen.',
  'hint.line2': 'De wiskunde leeft in de figuur, niet in tekst.',
  // Coordinate-geometry additions
  'coord.panel.ratio-m': 'Verhouding m',
  'coord.panel.ratio-n': 'Verhouding n',
  'coord.panel.show-angles': 'Toon hoeken',
  // Transformations — symmetry redesign
  'transform.symmetry.drag-hint': 'Sleep de spiegellijn',
  'transform.symmetry.axis-found': 'Symmetrieas ✓',
  'transform.symmetry.axes': 'Spiegellijnen',
  'transform.symmetry.rotation-order': 'Rotatieorde',
  'transform.symmetry.spin': 'Draai',
  // Constructions — tool options
  'construct.tool.compass': 'Passer',
  'construct.tool.straightedge': 'Liniaal',
  'construct.tool.eraser': 'Gummetje',
  // Shared badges
  'badge.parallel': '∥',
  'badge.perpendicular': '⊥',
  'badge.similar': '~',
  'badge.invalid': '✗',
  // Angle-chase specific
  'angle.mode.around-a-point': 'Rondom een punt',
  'angle.mode.parallel-lines': 'Evenwijdige lijnen',
  'angle.mode.polygon-angles': 'Veelhoekhoeken',
  'angle.mode.double-transversal': 'Dubbele transversaal',
  'angle.panel.angles-on-line': 'Hoeken op een lijn',
  'angle.panel.show-angle-values': 'Toon hoekwaarden',
  'angle.panel.snap-to-5': 'Klik op 5°',
  'angle.panel.n-gon-sides': 'Zijden (n)',
  'angle.preset.straight-line': 'Rechte lijn',
  'angle.preset.full-turn': 'Volle omwenteling',
  'angle.preset.z-angles': 'Z-hoeken',
  'angle.preset.c-angles': 'C-hoeken',
  'angle.preset.f-angles': 'F-hoeken',
  'angle.preset.pentagon-fan': 'Vijfhoekwaaier',
  'angle.preset.hexagon-turtle': 'Zeshoek schildpadwandeling',
  'angle.preset.double-transversal': 'Dubbele transversaal',
  // Triangle-lab specific
  'triangle.mode.free-drag': 'Vrij slepen',
  'triangle.mode.inequality': 'Ongelijkheid',
  'triangle.mode.congruence': 'Congruentie',
  'triangle.criterion.SSS': 'ZZZ',
  'triangle.criterion.SAS': 'ZHZ',
  'triangle.criterion.ASA': 'HZH',
  'triangle.criterion.AAS': 'HHZ',
  'triangle.criterion.SSA': 'ZZH',
  'triangle.panel.show-measurements': 'Toon maten',
  'triangle.panel.center-centroid': 'Zwaartelijn',
  'triangle.panel.center-circumcenter': 'Omcirkel middelpunt',
  'triangle.panel.center-incenter': 'Incirkel middelpunt',
  'triangle.panel.center-orthocenter': 'Hoogtepunt',
  'triangle.preset.equilateral': 'Gelijkzijdig',
  'triangle.preset.isosceles-right': 'Gelijkbenig rechthoekig',
  'triangle.preset.scalene-obtuse': 'Ongelijkzijdig stomphoekig',
  'triangle.preset.euler-tour': 'Euler-lijn rondleiding',
  'triangle.preset.ssa-two': 'ZZH: twee oplossingen',
  'triangle.criterion': 'Criterium',
  // Pythagoras specific
  'pythagoras.mode.squares': 'Vierkanten',
  'pythagoras.mode.converse': 'Omgekeerde stelling',
  'pythagoras.mode.triples': 'Pythagorische drietallen',
  'pythagoras.mode.shapes': 'Vormen',
  'pythagoras.panel.show-areas': 'Toon oppervlakten',
  'pythagoras.panel.shape': 'Vorm',
  'pythagoras.panel.scrubber': 'Bewijs scrubber',
  'pythagoras.shape.square': 'Vierkant',
  'pythagoras.shape.semicircle': 'Halve cirkel',
  'pythagoras.shape.equilateral': 'Gelijkzijdige driehoek',
  'pythagoras.preset.3-4-5': '3–4–5',
  'pythagoras.preset.5-12-13': '5–12–13',
  'pythagoras.preset.1-1-sqrt2': '1–1–√2',
  'pythagoras.preset.8-15-17': '8–15–17',
  'pythagoras.preset.almost-right-4-5-6': 'Bijna rechthoekig (4–5–6)',
  // Similarity specific
  'similarity.mode.dilation': 'Vergroting',
  'similarity.mode.intercept': 'Intercept-stelling',
  'similarity.mode.similar-triangles': 'Gelijkvormige driehoeken',
  'similarity.panel.k': 'Schaalfactor k',
  'similarity.panel.show-rays': 'Toon stralen',
  'similarity.panel.show-area-grid': 'Toon oppervlaktegrid',
  'similarity.preset.midsegment-half': 'Middellijn (k=½)',
  'similarity.preset.double-k-2': 'Dubbel (k=2)',
  'similarity.preset.inversion-k-minus-1': 'Inversie (k=−1)',
  'similarity.preset.aa-detective': 'HH-speurder',
  // Circle-theorems specific
  'circle.mode.inscribed': 'Ingeschreven hoek',
  'circle.mode.tangent': 'Raaklijn',
  'circle.mode.cyclic-quad': 'Cyclische vierhoek',
  'circle.mode.alternate-segment': 'Wisselende boog',
  'circle.mode.power-of-point': 'Machtslijn',
  'circle.panel.show-central-angle': 'Toon middelpuntshoek',
  'circle.panel.snap-to-diameter': 'Vergrendel op diameter',
  'circle.preset.thales': 'Thales (diameter)',
  'circle.preset.minor-arc': 'Kleine boog',
  'circle.preset.crossed-chords': 'Gekruiste koorden',
  'circle.preset.tangent-secant': 'Raaklijn–snijlijn',
  // Area-lab specific
  'area.panel.shape': 'Vorm',
  'area.panel.shear': 'Afschuiving',
  'area.panel.sectors': 'Sectoren (n)',
  'area.panel.polygon-n': 'Zijden (n)',
  'area.panel.scrubber': 'Afleiding scrubber',
  'area.panel.show-grid': 'Toon raster',
  'area.shape.parallelogram': 'Parallellogram',
  'area.shape.triangle': 'Driehoek',
  'area.shape.trapezoid': 'Trapezium',
  'area.shape.circle': 'Cirkel',
  'area.shape.regular-polygon': 'Regelmatige veelhoek',
  'area.shape.composite': 'Samengesteld (L-vorm)',
  'area.shape.heron': 'Heron kruiscontrole',
  'area.preset.parallelogram': 'Parallellogram (6×4)',
  'area.preset.triangle': 'Driehoek (6×4)',
  'area.preset.trapezoid': 'Trapezium (3–5, h=4)',
  'area.preset.circle': 'Cirkel (r=4)',
  'area.preset.hexagon-apothem': 'Zeshoek apothema',
  'area.preset.l-shape': 'L-vorm samengesteld',
  'area.preset.heron-check': 'Heron controle (5–6–7)',
  // Transformations specific
  'transform.mode.transform': 'Transformatie',
  'transform.mode.symmetry': 'Symmetrie',
  'transform.mode.composition': 'Samenstelling',
  'transform.mode.tessellation': 'Betegeling',
  'transform.picker.translate': 'Verschuiving',
  'transform.picker.rotate': 'Rotatie',
  'transform.picker.reflect': 'Spiegeling',
  'transform.picker.dilate': 'Vergroting',
  'transform.panel.transform': 'Transformatie',
  'transform.panel.figure': 'Figuur',
  'transform.panel.tile': 'Tegel',
  'transform.panel.dilate-k': 'Schaalfactor k',
  'transform.tile.square': 'Vierkant',
  'transform.tile.hexagon': 'Zeshoek',
  'transform.tile.triangle': 'Driehoek',
  'transform.tile.escher-square': 'Escher vierkant',
  'transform.preset.quarter-turn': 'Kwartslag',
  'transform.preset.mirror-flip': 'Spiegelomkering',
  'transform.preset.parallel-mirrors': 'Parallelle spiegels',
  'transform.preset.crossing-mirrors': 'Kruisende spiegels',
  'transform.preset.hexagon-tiling': 'Zeshoekige betegeling',
  // Coordinate-geometry specific
  'coord.mode.segment': 'Lijnstuk',
  'coord.mode.parallel-perpendicular': 'Evenwijdig en loodrecht',
  'coord.mode.section': 'Deelformule',
  'coord.mode.shoelace': 'Schoenveteroppervlak',
  'coord.mode.locus': 'Meetkundige plaats (middelloodlijn)',
  'coord.panel.show-rise-run': 'Toon stijg/loop driehoek',
  'coord.panel.ratio-mn': 'Verhouding m : n',
  'coord.preset.3-4-5-segment': 'Lijnstuk 3–4–5',
  'coord.preset.perpendicular-pair': 'Loodrecht paar',
  'coord.preset.section-2-1': 'Deling 2 : 1',
  'coord.preset.lattice-pentagon': 'Roosterpentagon',
  // Constructions — target labels
  'construct.target.perpendicular-bisector': 'Middelloodlijn',
  'construct.target.angle-bisector': 'Bissectrice',
  'construct.target.copy-segment': 'Kopieer lijnstuk',
  'construct.target.copy-angle': 'Kopieer hoek',
  'construct.target.perpendicular-at-point': 'Loodlijn in punt',
  'construct.target.perpendicular-from-point': 'Loodlijn vanuit punt',
  'construct.target.inscribed-triangle': 'Ingeschreven driehoek',
  'construct.target.inscribed-square': 'Ingeschreven vierkant',
  'construct.target.inscribed-hexagon': 'Ingeschreven zeshoek',
  'construct.target.tangent-from-point': 'Raaklijn vanuit punt',
  'construct.target.incircle': 'Ingeschreven cirkel',
  'construct.target.circumcircle': 'Omgeschreven cirkel',
  'construct.target.angle-60': '60° hoek',
  'construct.target.angle-30': '30° hoek',
  'construct.target.angle-45': '45° hoek',
  // Constructions — panel keys
  'construct.panel.target': 'Constructie',
  'construct.panel.show-ghost': 'Toon doel',
  // Constructions — readout keys
  'construct.readout.goal-met': 'Constructie voltooid',
  'construct.readout.working': 'Bezig…',
  // Constructions — scripted step captions
  'perpBisector.arc1': 'Boog vanuit A',
  'perpBisector.arc2': 'Boog vanuit B',
  'perpBisector.line': 'Verbind snijpunten',
  'angleBisector.arcFromVertex': 'Boog vanuit hoekpunt',
  'angleBisector.arc2': 'Boog vanuit been A',
  'angleBisector.arc3': 'Boog vanuit been B',
  'angleBisector.line': 'Teken bissectrice',
  'copySegment.arc': 'Passer op segmentlengte',
  'copyAngle.arc1': 'Boog bij nieuw hoekpunt',
  'copyAngle.arc2': 'Kopieer koorde — nabije zijde',
  'copyAngle.arc3': 'Kopieer koorde — verre zijde',
  'copyAngle.line': 'Teken gekopieerde hoekstraal',
  'perpAtPoint.arc1': 'Boog gecentreerd in P',
  'perpAtPoint.arc2': 'Grote boog vanuit linkervoet',
  'perpAtPoint.arc3': 'Grote boog vanuit rechtervoet',
  'perpAtPoint.line': 'Verbind snijpunten',
  'perpFromPoint.arc1': 'Boog vanuit extern punt',
  'perpFromPoint.arc2': 'Grote boog vanuit linkervoet',
  'perpFromPoint.arc3': 'Grote boog vanuit rechtervoet',
  'perpFromPoint.line': 'Verbind snijpunten',
  'inscribedHex.s1': 'Stap 1 — volgend hoekpunt',
  'inscribedHex.s2': 'Stap 2 — volgend hoekpunt',
  'inscribedHex.s3': 'Stap 3 — volgend hoekpunt',
  'inscribedHex.s4': 'Stap 4 — volgend hoekpunt',
  'inscribedHex.s5': 'Stap 5 — volgend hoekpunt',
  'inscribedHex.side1': 'Teken zijde',
  'inscribedHex.side2': 'Teken zijde',
  'inscribedTri.s1': 'Boog — eerste hoekpunt',
  'inscribedTri.s2': 'Boog — tweede hoekpunt',
  'inscribedTri.s3': 'Boog — derde hoekpunt',
  'inscribedTri.side1': 'Zijde 1',
  'inscribedTri.side2': 'Zijde 2',
  'inscribedTri.side3': 'Zijde 3',
  'inscribedSquare.s1': 'Boog vanuit P₀',
  'inscribedSquare.s2': 'Boog vanuit antipode',
  'inscribedSquare.diam': 'Loodrechte diameter',
  'inscribedSquare.side1': 'Zijde 1',
  'inscribedSquare.side2': 'Zijde 2',
  'tangent.thalesCircle': 'Thales-cirkel',
  'tangent.line1': 'Eerste raaklijn',
  'tangent.line2': 'Tweede raaklijn',
  'incircle.arcA': 'Boog bij hoekpunt A',
  'incircle.arc2': 'Bissectrice boog 2',
  'incircle.arc3': 'Bissectrice boog 3',
  'incircle.bisA': 'Bissectrice bij A',
  'incircle.arcB': 'Boog bij hoekpunt B',
  'incircle.arc6': 'Bissectrice boog 6',
  'incircle.arc7': 'Bissectrice boog 7',
  'incircle.bisB': 'Bissectrice bij B',
  'incircle.footArc': 'Boog voor voetpunt',
  'incircle.perpArc1': 'Loodlijn boog 1',
  'incircle.perpArc2': 'Loodlijn boog 2',
  'incircle.perpLine': 'Loodlijn',
  'incircle.draw': 'Teken ingeschreven cirkel',
  'circumcircle.arcA1': 'Boog A (1)',
  'circumcircle.arcA2': 'Boog A (2)',
  'circumcircle.bisAB': 'Bisecteer AB',
  'circumcircle.arcC1': 'Boog C (1)',
  'circumcircle.arcC2': 'Boog C (2)',
  'circumcircle.bisAC': 'Bisecteer AC',
  'circumcircle.draw': 'Teken omgeschreven cirkel',
  'angle60.arc1': 'Boog bij oorsprong',
  'angle60.arc2': 'Boog bij Q',
  'angle60.line': 'Teken 60° straal',
  'angle30.arc1': 'Boog bij oorsprong',
  'angle30.arc2': 'Boog bij Q',
  'angle30.line60': 'Teken 60° lijn',
  'angle30.bisArc1': 'Bissectrice boog bij oorsprong',
  'angle30.bisArc2': 'Bissectrice boog 2',
  'angle30.bisArc3': 'Bissectrice boog 3',
  'angle30.line': 'Teken 30° straal',
  'angle45.perpArc1': 'Boog bij oorsprong',
  'angle45.perpArc2': 'Boog — nabij voetpunt',
  'angle45.perpArc3': 'Boog — ver voetpunt',
  'angle45.line90': 'Teken 90° lijn',
  'angle45.bisArc1': 'Bissectrice boog bij oorsprong',
  'angle45.bisArc2': 'Bissectrice boog 2',
  'angle45.bisArc3': 'Bissectrice boog 3',
  'angle45.line': 'Teken 45° straal',
  // Per-experiment help legend lines
  'help.angle-chase.1': 'Sleep de gekleurde punten om de hoeken te openen of te sluiten.',
  'help.angle-chase.2':
    'Elke boog toont de hoek bij dat hoekpunt; gelijke streepjes markeren gelijke hoeken.',
  'help.angle-chase.3':
    'Parallelle pijlen markeren evenwijdige stralen; het display bewijst de hoeksom.',
  'help.triangle-lab.1': 'Sleep de drie gekleurde hoekpunten om de driehoek te hervormen.',
  'help.triangle-lab.2': 'Zijstreepjes markeren gelijke zijden; elke boog markeert een binnenhoek.',
  'help.triangle-lab.3':
    'Het gekleurde punt is het gekozen middelpunt; het display toont de maten.',
  'help.pythagoras.1': 'Sleep de gekleurde hoekpunten om de rechthoekszijden a en b te wijzigen.',
  'help.pythagoras.2':
    'Op elke zijde staat een vierkant of figuur, met de oppervlakte erin geschreven.',
  'help.pythagoras.3':
    'Het display toont a² + b² = c²: de twee kleinere oppervlakten tellen op tot de grootste.',
  'help.similarity.1': 'Sleep de gekleurde punten; het dilatatiemiddelpunt is het gekleurde punt.',
  'help.similarity.2':
    'Stralen vanuit het middelpunt beelden het gestreepte origineel af op het vlakke beeld.',
  'help.similarity.3':
    'Schaalfactor k geeft aan hoeveel groter het beeld is; de oppervlakte groeit met k².',
  'help.circle-theorems.1': 'Sleep de gekleurde punten langs de cirkel.',
  'help.circle-theorems.2':
    'De boog markeert de ingeschreven hoek; het middelpunt markeert de middelpuntshoek.',
  'help.circle-theorems.3': 'Het display bewijst de cirkelstelling voor de huidige modus.',
  'help.area-lab.1':
    'Kies een vorm en sleep de schuifregelaars om de grootte of afschuiving te wijzigen.',
  'help.area-lab.2':
    'De oppervlakte staat op de vorm geschreven; afschuiven houdt de oppervlakte gelijk.',
  'help.area-lab.3':
    'De afleiding-schuifregelaar rangschikt de vorm tot een rechthoek met gelijke oppervlakte.',
  'help.transformations.1': 'De gestreepte vorm is het origineel; de gekleurde vorm is het beeld.',
  'help.transformations.2':
    'Bij Symmetrie, sleep de spiegellijn — hij licht op bij een echte symmetrieas.',
  'help.transformations.3': 'Draaien onthult rotatiesymmetrie; het display toont assen en orde.',
  'help.coordinate-geometry.1': 'Sleep de gekleurde punten A en B op het rooster.',
  'help.coordinate-geometry.2':
    'De gestreepte driehoek toont stijging en loop; de boog toont de hoek.',
  'help.coordinate-geometry.3': 'Het display geeft exacte afstanden, hellingen en oppervlakten.',
  'help.constructions.1': 'Kies een gereedschap (passer of liniaal) en een doelconstructie.',
  'help.constructions.2':
    'De vage gestreepte vorm is het doel; klikpunten tonen waar je kunt klikken.',
  'help.constructions.3':
    'Als je tekening overeenkomt met het doel, licht het op en bevestigt de badge het.',
};
